import React, { useEffect, useState } from 'react';
import Modal from '../common/Modal';
import { DataCell, CommonModal } from '../../TableUIKit';
import { useEmailApi } from '../../auth/helper/api/emailApi';

const EmailTemplatePage = () => {

  const emailApi = useEmailApi();

  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]); // State for categories
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false); // State for modal visibility
  const [newTemplate, setNewTemplate] = useState({
    emailSubject: '',
    emailTemplateBody: '',
    emailTemplateCode: '',
    emailTemplateName: '',
    emailBodyType: '',
    activationStatus: true, // Default value
    categoryId: '',
    createdAt: new Date().toISOString(), // Default value
    lastUpdatedAt: new Date().toISOString(), // Default value
  });

  // State for filters
  const [filters, setFilters] = useState({
    id: null,
    emailTemplateName: '',
    emailSubject: '',
    categoryId: '',
    activationStatus: true, // Default value
  });

  //for handling pagination
  const [page, setPage] = useState(0); // Starting from the first page
  const [size, setSize] = useState(3); // Number of items per page
  const [totalPages, setTotalPages] = useState(0); // To store total pages

  const [activeDetails, setActiveDetails] = useState(null);


  useEffect(() => {

    const fetchTemplates = async () => {
      try {
        const response = await emailApi.get(
          process.env
            .REACT_APP_GET_ALL_EMAIL_TEMPLATES_BY_PAGE_ENDPOINT,
          {
            params: {
              ...filters,
              page,
              size,
            },
          }
        );

        // Extract data from the response
        const { message, object, httpStatus } = response.data;

        if (httpStatus === 200) { // Check for successful status
          setTemplates(object.content); // Set the templates from the response object
          setTotalPages(object.totalPages);
        } else {
          setError(`Error: ${message}`); // Handle non-success status code
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching templates:', err);
        setError(err.message || 'An unexpected error occurred.'); // Provide error message to state
        setLoading(false);
      }
    };

    const fetchCategories = async () => {

      try {

        const response = await emailApi.get(
          process.env.REACT_APP_GET_ALL_EMAIL_CATEGORIES_ENDPOINT
        );
        // Extract data from the response
        const { message, object, httpStatus } = response.data;

        if (httpStatus === 200) { // Check for successful status
          setCategories(object); // Set the templates from the response object
        } else {
          setError(`Error: ${message}`); // Handle non-success status code
        }
        setLoading(false);

      } catch (err) {
        console.error('Error fetching categories:', err);
        setError(err.message || 'Failed to load categories.');
        setLoading(false);
      }
    };

    fetchTemplates();
    fetchCategories();
  }, [emailApi, page, size]); // Dependency array is empty to run this effect once after the component mounts

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewTemplate((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value, // Handle checkbox uniquely
    }));
  };

  const handleSubmit = async (e) => {

    e.preventDefault();
    try {
      const response = await emailApi.post(
        `${process.env.REACT_APP_SET_EMAIL_TEMPLATE_ENDPOINT}`,
        newTemplate
      );
      if (response.status === 200) {
        setTemplates((prev) => [...prev, response.data.object]); // Append the new template
        setNewTemplate({ // Reset form
          emailSubject: '',
          emailTemplateBody: '',
          emailTemplateCode: '',
          emailTemplateName: '',
          emailBodyType: '',
          activationStatus: true,
          categoryId: '',
        });
        setError(null); // Clear any previous errors
        setModalOpen(false); // Close modal after submission
      } else {
        setError(`Error: ${response.data.message}`);
      }
    } catch (err) {
      setError(err.message || 'An error occurred while creating the template.');
    }
  };

  const handleFilterInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const newFilters = { ...prev, [name]: value };
      return newFilters;
    });
  };

  // Handle the status change to convert to boolean
  const handleStatusChange = (e) => {
    const value = e.target.value;
    const statusValue = value === 'Active' ? true : value === 'Inactive' ? false : null;

    setFilters((prev) => {
      const newFilters = { ...prev, activationStatus: statusValue };
      return newFilters;
    });
  };

  const toggleActivationStatus = async (templateId, currentStatus) => {

    try {
      const newStatus = !currentStatus; // Toggle activation status
      await emailApi.patch(
        `${process.env.REACT_APP_SET_EMAIL_TEMPLATE_STATUS_ENDPOINT}/${templateId}`,
        { activationStatus: newStatus }
      );
      // Update the local state after toggling the status
      setTemplates((prev) => prev.map(template =>
        template.id === templateId ? { ...template, activationStatus: newStatus } : template
      ));
    } catch (err) {
      setError(err.message || 'An error occurred while updating the status.');
    }
  };

  // New function to apply filters
  const applyFilters = async () => {

    setLoading(true); // Show loading while fetching filtered notifications
    try {
      const response = await emailApi.get(
        process.env
          .REACT_APP_GET_ALL_EMAIL_TEMPLATES_BY_PAGE_ENDPOINT,
        {
          params: {
            ...filters,
            page,
            size,
          },
        }
      );

      // Extract data from the response
      const { message, object, httpStatus } = response.data;

      if (httpStatus === 200) { // Check for successful status
        setTemplates(object.content); // Set the templates from the response object
        setTotalPages(object.totalPages);
      } else {
        setError(`Error: ${message}`); // Handle non-success status code
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.message || 'An unexpected error occurred.'); // Provide error message to state
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (page + 1 < totalPages) {
      setPage((prevPage) => prevPage + 1); // Increment page number
    }
  };

  const handlePreviousPage = () => {
    if (page > 0) {
      setPage((prevPage) => prevPage - 1); // Decrement page number
    }
  };

  const handleSizeChange = (e) => {
    setSize(e.target.value);
    setPage(0); // Reset to first page on changing size
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading templates: {error}</div>;

  return (
    <div className="template-table">
      <h2>Email Templates</h2>
      <div className="flex-row filter-section">
        <div className='filter-item'>
          <label htmlFor="items-per-page">Items per page: </label>
          <select id="items-per-page" onChange={handleSizeChange} className="w-auto">
            <option value={3}>default</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={5}>5</option>
            <option value={10}>10</option>
          </select>
        </div>
      </div>

      <h3>Filters</h3>
      {/* Filter Form */}
      <div className="flex-row filter-section">
        <div className="filter-item">
          <label>Id:</label>
          <input
            type="text"
            name="id"
            value={filters.id ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>emailTemplateName:</label>
          <input
            type="text"
            name="emailTemplateName"
            value={filters.emailTemplateName ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>emailSubject:</label>
          <input
            type="text"
            name="emailSubject"
            value={filters.emailSubject ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>categoryId:</label>
          <input
            type="text"
            name="categoryId"
            value={filters.categoryId ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>


        <div className="filter-item">
          <label>Status:</label>
          <select name="activationStatus" onChange={handleStatusChange} value={filters.activationStatus === null ? '' : filters.activationStatus ? 'Active' : 'Inactive'}>
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <button type="button" onClick={applyFilters}>Apply Filters</button>

      </div>

      {/* Button to open the modal */}
      <button onClick={() => setModalOpen(true)}>+ Create New</button>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <div className="page-container">
          <h3>Create New Email Template</h3>
          {/* Form for creating a new Email Template */}
          <form onSubmit={handleSubmit} className="standard-form">
            <div className="form-group">
              <label>Email Subject:</label>
              <input
                type="text"
                name="emailSubject"
                value={newTemplate.emailSubject}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Email Template Body:</label>
              <textarea
                name="emailTemplateBody"
                value={newTemplate.emailTemplateBody}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Email Template Code:</label>
              <input
                type="text"
                name="emailTemplateCode"
                value={newTemplate.emailTemplateCode}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Email Template Name:</label>
              <input
                type="text"
                name="emailTemplateName"
                value={newTemplate.emailTemplateName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Email Body Type:</label>
              <input
                type="text"
                name="emailBodyType"
                value={newTemplate.emailBodyType}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Activation Status:</label>
              <input
                type="checkbox"
                name="activationStatus"
                checked={newTemplate.activationStatus}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Category:</label>
              <select
                name="categoryId"
                value={newTemplate.categoryId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.emailCategory}
                  </option>
                ))}
              </select>
            </div>


            <button type="submit">Create Template</button>
          </form>
        </div>
      </Modal>

      <h3>Existing Email Templates</h3>
      {templates.length === 0 ? (
        <p>No templates available</p>
      ) : (
        <div className="table-responsive">
          <table className="notifications-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Template Name</th>
                <th>Subject</th>
                <th>Body Type</th>
                <th>Template Body</th>
                <th>Activation Status</th>
                <th>Created At</th>
                <th>Last Updated At</th>
                <th>Last Updated By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.id}>
                  <td>{template.id}</td>
                  <td>{template.emailTemplateName}</td>
                  <td>{template.emailSubject}</td>
                  <td>{template.emailBodyType}</td>
                  <DataCell label="Template Body" value={template.emailTemplateBody} onClick={setActiveDetails} />
                  <td>{template.activationStatus ? 'Active' : 'Inactive'}</td>
                  <td>{new Date(template.createdAt).toLocaleString()}</td>
                  <td>{new Date(template.lastUpdatedAt).toLocaleString()}</td>
                  <td>{template.lastUpdatedBy}</td>

                  <td>
                    <button onClick={() => toggleActivationStatus(template.id, template.activationStatus)}>
                      {template.activationStatus ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Pagination Controls */}
      <div className="pagination-container">
        <button onClick={handlePreviousPage} disabled={page === 0}>Previous</button>
        <span>Page {page + 1} of {totalPages}</span>
        <button onClick={handleNextPage} disabled={page + 1 === totalPages}>Next</button>
      </div>
      {/* ONE MODAL TO RULE THEM ALL */}
      <CommonModal
        isOpen={!!activeDetails}
        onClose={() => setActiveDetails(null)}
        /* Pull the dynamic title from state */
        title={activeDetails?.title || "Details"}
        copyValue={activeDetails?.text}
      >
        {activeDetails?.text}
      </CommonModal>
    </div>
  );
};

export default EmailTemplatePage;
